import React, { useCallback } from 'react'
import cx from 'classnames'
import cookieStorage from 'cookie-storage'
import { useEntry } from 'intersection-observer'
import links from 'links'
import { track } from 'analytics'
import { useDevice } from 'device'
import { useHistory } from 'router'
import { constants } from 'helpers'
import { usePlanPricesList } from 'modules/subscription'

import { Text } from 'components/dataDisplay'
import { Bone } from 'components/layout'

import UpgradeLargeBlock from 'compositions/blocks/UpgradeLargeBlock/UpgradeLargeBlock'
import UpgradeCompactBlock from './components/UpgradeCompactBlock/UpgradeCompactBlock'

import messages from './messages'

import s from './PlanSelectSection.scss'


type PlanSelectSectionProps = {
  className?: string
  couponCode: string
}

const PlanSelectSection: React.FC<PlanSelectSectionProps> = ({ className, couponCode }) => {
  const { isMobile } = useDevice()

  const [ ref, entry ] = useEntry({ once: true, observerProps: { rootMargin: '50%' } })
  const skip = !entry?.isIntersecting

  const { data, isFetching } = usePlanPricesList({
    productsPerPeriod: null, // to remove default "1"
    couponCode,
    skip,
  })

  const discount = data?.[0]?.discountPercent

  const history = useHistory()

  const onPlanSelect = useCallback((plan: SubscriptionModule.UpgradePlan) => {
    track('Subscription plan choose click', {
      numberOfProducts: plan.productsPerPeriod,
    })

    cookieStorage.setSessionItem(constants.cookieNames.selectedPlan, plan.name)

    history.push(links.subscribe)
  }, [ history ])

  const plans = data?.map(({ plan }) => plan)

  const discountHeaderClassName = cx(
    s.discountHeader,
    isMobile ? 'pt-24' : 'pt-60 max-w-1440 mx-auto',
    'bg-light-beige text-center'
  )

  const circleClassName = cx(
    s.circle,
    'mx-auto flex flex-col justify-center text-white text-center bg-gold-50 radius-full'
  )

  return (
    <>
      <div className={className} ref={ref} />
      {
        skip || isFetching ? (
          <Bone pw={100} h={isMobile ? 1054 : 1200} bgColor="light-beige" />
        ) : plans?.length > 1 && (
          <>
            {
              Boolean(discount) && (
                <div className={discountHeaderClassName}>
                  <div className={circleClassName}>
                    <div className="text-h2">{discount}</div>
                    <Text message={messages.label} style="h4" />
                  </div>
                  <Text
                    className={cx(isMobile ? 'mx-16' : 'max-w-752 mx-auto', 'mt-24')}
                    message={{ ...messages.title, values: { discount: discount.replace('%', '') } }}
                    style={isMobile ? 'h2' : 'h1'}
                    color="gold-50"
                  />
                </div>
              )
            }
            {
              isMobile ? (
                <UpgradeCompactBlock
                  title={messages.subTitle}
                  text={messages.text}
                  plans={plans}
                  onPlanSelect={onPlanSelect}
                />
              ) : (
                <UpgradeLargeBlock
                  title={messages.subTitle}
                  text={messages.text}
                  plans={plans}
                  onPlanSelect={onPlanSelect}
                  isSubscriptionFlow
                />
              )
            }
          </>
        )
      }
    </>
  )
}


export default PlanSelectSection
